import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import BarGraph from "./BarGraph";

// Import certificate images
import NvidiaImage from "../../assets/files/NVDIA.jpg";
import Neo4jImage from "../../assets/files/Neo4j.jpg";
import AWS from "../../assets/files/aws.webp";
import Apdf from "../../assets/files/AWS.pdf";
import Npdf from "../../assets/files/NVIDIA.pdf";
export default function Resume() {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  };
  /*
    {
      title: "Cisco Intro to Software Engineering",
      issuer: "Cisco via Forage",
      date: "August 4, 2023",
      image: Cisco,
      link: "https://forage-uploads-prod.s3.amazonaws.com/completion-certificates/Cisco/kinDTvjiZRcYbwqLo_Cisco_3dr8H2hQyoawrwMtT_1691185178215_completion_certificate.pdf",
      description:
        "Developed understanding of Cisco's approach to problem-solving and software engineering practices",
    },

*/
  const timelineItems = [
    {
      title: "Software Engineering Intern at Bercerita",
      period: "June 2025 - Present",
      description:
        "While interning at Bercerita, I've been helping build web applications that support language learning for students. I work on both the frontend and backend, making sure features are reliable, secure, and easy to use. This includes developing user interfaces, building secure APIs, and helping deploy updates to the cloud. I also collaborate closely with other interns and the product manager to plan features, prioritize tasks, and ensure the platform meets user needs.",
      type: "education",
      icon: "🚀",
      status: "current",
    },
    {
      title: "BS in Computer Science",
      period: "Completed on May 22, 2025",
      description:
        "During my time at San Jose State University, I'm planning to obtain my bachelor's in computer science while being more involved in the community and gaining more experience.",
      type: "education",
      icon: "🎓",
      status: "completed",
    },
    {
      title: "AS in Computer Science & Mathematics",
      period: "Completed on May 25th, 2023",
      description:
        "During my time at Diablo Valley College, I learned important computer science concepts and independently created applications. I took part in hackathons to test my skills and improve my ability to lead teams.",
      type: "education",
      icon: "🎓",
      links: [
        {
          text: "CS Certificate",
          url: "https://www.parchment.com/u/award/56f679997bfe5c39f12fda2c860f7a2d/file",
        },
        {
          text: "Math Certificate",
          url: "https://www.parchment.com/u/award/a0171587d72c47699bf2d0b4b2231ef6/file",
        },
      ],
      status: "completed",
    },
  ];
  void timelineItems;

  const resumeTimelineItems = [
    {
      title: "Software Engineer at PluckLabs",
      period: "January 2026 - Present",
      description:
        "Built backend and platform improvements for PluckLabs, a platform serving 50,000+ users. I created a cron job and designed its database diagram so unviewed support and sales tickets are surfaced to admins before they are missed. I also built a Google Cloud Function that injects SEO metadata in Markdown and JSON-LD, improving how Pluck sites are indexed by search engines and AI agents. My work also involved Firebase bucket storage and database lookups, competitor-based SEO strategy research, editor and publishing bug fixes, integration testing, and Agile development within the team's CI/CD workflow, along with direct customer support when issues came up.",
      type: "experience",
      icon: "💼",
      status: "current",
    },
    {
      title: "Software Engineering Intern at Bercerita",
      period: "June 2025 - January 2026",
      description:
        "Worked as an early engineering contributor on a language-learning platform, helping shape product direction while building across the stack. I designed database schemas, developed frontend flows and backend APIs, built an AWS Transcribe voice-to-text workflow for the site's chatbot feature, monitored deployment logs, and supported CI/CD through GitHub Actions. I worked in an Agile process with Jira tickets and communicated directly with the PM, PO, and UI/UX designers to turn requirements into working features.",
      type: "experience",
      icon: "💻",
      status: "completed",
    },
    {
      title: "BS in Computer Science",
      period: "Completed on May 22, 2025",
      description:
        "Completed my Bachelor's in Computer Science at San Jose State University while continuing to build projects, grow as an engineer, and stay involved in hands-on technical work outside the classroom.",
      type: "education",
      icon: "🎓",
      status: "completed",
    },
    {
      title: "AS in Computer Science & Mathematics",
      period: "Completed on May 25th, 2023",
      description:
        "Built a strong foundation in computer science and math at Diablo Valley College while independently creating applications and participating in hackathons to sharpen technical and leadership skills.",
      type: "education",
      icon: "🎓",
      links: [
        {
          text: "CS Certificate",
          url: "https://www.parchment.com/u/award/56f679997bfe5c39f12fda2c860f7a2d/file",
        },
        {
          text: "Math Certificate",
          url: "https://www.parchment.com/u/award/a0171587d72c47699bf2d0b4b2231ef6/file",
        },
      ],
      status: "completed",
    },
  ];

  const certifications = [
    {
      title: "Getting Started with Deep Learning",
      issuer: "NVIDIA",
      date: "September 2025",
      image: NvidiaImage,
      link: Npdf,
      description:
        "Demonstrated competence in completing NVIDIA's Deep Learning fundamentals course",
    },
    {
      title: "AWS Certified Cloud Practitioner",
      issuer: "Amazon Web Services",
      date: "September 2025",
      image: AWS,
      link: Apdf,
      description:
        "Foundational level certification covering AWS Cloud concepts and services",
    },
    {
      title: "Neo4j Intermediate Cypher Queries",
      issuer: "Neo4j",
      date: "November 2024",
      image: Neo4jImage,
      link: "https://graphacademy.neo4j.com/c/d778753f-a917-4f62-814f-2959c43eccf5/",
      description:
        "Advanced querying capabilities in Neo4j graph database using Cypher",
    },
  ];

  const skills = [
    {
      name: "Next.js",
      command: "install nextjs@latest",
      output: "App router, SSR, API routes, and production-ready UI shipped.",
      icon: "▲",
      accent: "text-cyan-400",
      badgeClass: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
    },
    {
      name: "React.js",
      command: "compile react-components",
      output: "Built reusable interfaces, motion-driven sections, and polished UX.",
      icon: "◌",
      accent: "text-sky-400",
      badgeClass: "border-sky-500/30 bg-sky-500/10 text-sky-300",
    },
    {
      name: "TypeScript",
      command: "tsc --noEmit",
      output: "Used typed frontend patterns and safer application logic.",
      icon: "{}",
      accent: "text-indigo-400",
      badgeClass: "border-indigo-500/30 bg-indigo-500/10 text-indigo-300",
    },
    {
      name: "Python",
      command: "python manage_platform.py",
      output: "Backend workflows, automation, APIs, and data-heavy app features.",
      icon: "py",
      accent: "text-emerald-400",
      badgeClass: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    },
    {
      name: "PostgreSQL / Supabase",
      command: "connect postgres://supabase",
      output: "Schema design, relational queries, and managed production data.",
      icon: "db",
      accent: "text-fuchsia-400",
      badgeClass: "border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-300",
    },
    {
      name: "AWS",
      command: "aws deploy && aws transcribe",
      output: "Cloud services, deployment support, and voice-processing workflows.",
      icon: "aws",
      accent: "text-amber-400",
      badgeClass: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    },
    {
      name: "Java / C++",
      command: "build core-systems",
      output: "Strong CS fundamentals across object-oriented and systems work.",
      icon: ">>",
      accent: "text-rose-400",
      badgeClass: "border-rose-500/30 bg-rose-500/10 text-rose-300",
    },
  ];

  return (
    <div
      id="resume"
      ref={ref}
      className="min-h-screen py-20 px-4 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="relative z-10 max-w-7xl mx-auto"
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="head text-5xl md:text-6xl lg:text-7xl font-bold mb-4 gradient-text">
            Resume
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-20">
          {/* Enhanced Timeline Section */}
          <motion.div variants={itemVariants}>
            <h3 className="text-3xl font-bold mb-8 text-center lg:text-left gradient-text">
              Education & Experience
            </h3>
            <div className="relative">
              {/* Enhanced Timeline Line with Gradient */}
              <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500 rounded-full shadow-lg shadow-blue-500/20"></div>

              <div className="space-y-12">
                {resumeTimelineItems.map((item, index) => (
                  <motion.div
                    key={index}
                    className="relative pl-20"
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    {/* Enhanced Timeline Dot with Icon */}
                    <motion.div
                      className="absolute left-4 top-4 w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full border-4 border-gray-900 flex items-center justify-center text-white text-sm shadow-lg"
                      whileHover={{ scale: 1.2 }}
                      transition={{ duration: 0.2 }}
                    >
                      {item.icon}
                    </motion.div>

                    {/* Status Indicator */}
                    {item.status === "current" && (
                      <motion.div
                        className="absolute left-6 top-6 w-4 h-4 bg-green-500/80 rounded-full"
                        animate={{ scale: [1, 1.3, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                    )}

                    <motion.div
                      className="glass p-6 rounded-2xl hover:shadow-xl transition-all duration-300 border border-white/10"
                      whileHover={{ scale: 1.02, y: -5 }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="text-xl font-bold text-white">
                          {item.title}
                        </h4>
                        {item.status === "current" && (
                          <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full border border-green-500/30">
                            Current
                          </span>
                        )}
                      </div>

                      <p className="text-blue-400 font-medium mb-4 flex items-center gap-2">
                        <span>📅</span>
                        {item.period}
                      </p>

                      <p className="text-gray-300 leading-relaxed mb-6">
                        {item.description}
                      </p>

                      {/* Enhanced Links Section */}
                      {item.links && (
                        <div className="flex flex-wrap gap-3">
                          {item.links.map((link, linkIndex) => (
                            <motion.a
                              key={linkIndex}
                              href={link.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-400 hover:text-blue-300 hover:bg-blue-500/30 transition-all duration-300 rounded-lg border border-blue-500/30"
                              whileHover={{ scale: 1.05 }}
                            >
                              <span>📄</span>
                              <span>{link.text}</span>
                            </motion.a>
                          ))}
                        </div>
                      )}
                    </motion.div>

                    {/* Connection Line to Next Item */}
                    {index < resumeTimelineItems.length - 1 && (
                      <motion.div
                        className="absolute left-7 top-16 w-0.5 h-8 bg-gradient-to-b from-purple-500/50 to-transparent"
                        initial={{ height: 0 }}
                        whileInView={{ height: 32 }}
                        transition={{ duration: 0.5, delay: index * 0.2 + 0.3 }}
                        viewport={{ once: true }}
                      />
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Skills Section */}
          <motion.div variants={itemVariants}>
            <h3 className="text-3xl font-bold mb-8 text-center lg:text-left gradient-text">
              Technical Skills
            </h3>
            <BarGraph skills={skills} />

            <motion.div
              className="mt-12 text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
            >
              <motion.button
                className="btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="/K.Moradpour-Résumé.pdf"
                  className="flex items-center gap-2"
                >
                  <span>Download Full Resume</span>
                  <motion.span
                    animate={{ y: [0, -2, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    👇
                  </motion.span>
                </a>
              </motion.button>
            </motion.div>
          </motion.div>
        </div>

        {/* New Certifications Section */}
        <motion.div variants={itemVariants} className="mb-16">
          <h3 className="text-3xl font-bold mb-12 text-center gradient-text">
            Professional Certifications
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                className="group h-full"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <motion.a
                  href={cert.link}
                  target="_blank"
                  rel="noreferrer"
                  className="block h-full"
                  whileHover={{ scale: 1.05, y: -10 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="glass p-8 rounded-2xl hover:shadow-2xl hover:shadow-blue-500/20 transition-all duration-300 border border-white/10 h-full flex flex-col">
                    {/* Certificate Image */}
                    <div className="relative mb-6 overflow-hidden rounded-xl">
                      <img
                        src={cert.image}
                        alt={cert.title}
                        className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                        onError={(e) => {
                          // Fallback to a gradient placeholder if image fails to load
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      {/* Fallback gradient placeholder */}
                      <div className="hidden w-full h-48 bg-gradient-to-br from-blue-500/30 to-purple-500/30 items-center justify-center rounded-xl">
                        <span className="text-4xl">🏆</span>
                      </div>

                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                        <span className="text-white text-sm font-medium px-3 py-1 bg-white/20 rounded-full backdrop-blur-sm">
                          View Certificate
                        </span>
                      </div>
                    </div>

                    {/* Certificate Info - flex-grow to fill remaining space */}
                    <div className="text-center flex-grow flex flex-col">
                      <h4 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors duration-300">
                        {cert.title}
                      </h4>

                      <p className="text-blue-400 font-medium mb-3 text-lg">
                        {cert.issuer}
                      </p>

                      <p className="text-gray-400 text-sm mb-4">{cert.date}</p>

                      <p className="text-gray-300 text-sm leading-relaxed flex-grow">
                        {cert.description}
                      </p>

                      {/* External link indicator */}
                      <div className="flex items-center justify-center mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-xs text-gray-400 flex items-center gap-1">
                          <span>🔗</span>
                          Click to view
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.a>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
